/**
 * Subsequence fuzzy matching for the command palette and lookup fields.
 *
 * Deliberately small and deterministic rather than a general fuzzy library: the
 * palette ranks a few hundred commands, and a scoring function whose behaviour
 * is obvious ("prefix beats word-start beats scattered") is worth more here than
 * a clever one. Server-side search is Postgres FTS + pg_trgm (CLAUDE.md §5);
 * this never pretends to be that.
 */

export interface FuzzyMatch {
  score: number
  /** Indices in the haystack that matched, for highlighting. */
  indices: number[]
}

const SCORE_EXACT = 1000
const SCORE_PREFIX = 500
const SCORE_WORD_START = 250
const SCORE_CONTAINS = 100
const SCORE_SEQUENTIAL = 15
const SCORE_CHARACTER = 5
const PENALTY_GAP = 1

function isWordBoundary(text: string, index: number): boolean {
  if (index === 0) return true
  const previous = text.charAt(index - 1)
  return previous === ' ' || previous === '-' || previous === '_' || previous === '/' || previous === '.'
}

/**
 * Score `needle` against `haystack`. Returns `null` when the needle is not a
 * subsequence of the haystack, so callers can filter and rank in one pass.
 */
export function fuzzyMatch(needle: string, haystack: string): FuzzyMatch | null {
  const query = needle.trim().toLowerCase()
  const target = haystack.toLowerCase()

  if (query === '') return { score: 0, indices: [] }
  if (query.length > target.length) return null

  if (target === query) {
    return { score: SCORE_EXACT, indices: [...target].map((_, index) => index) }
  }

  let score = 0
  if (target.startsWith(query)) score += SCORE_PREFIX
  else if (target.includes(query)) score += SCORE_CONTAINS

  const indices: number[] = []
  let cursor = 0
  let previousIndex = -2

  for (const character of query) {
    const found = target.indexOf(character, cursor)
    if (found === -1) return null

    indices.push(found)
    score += SCORE_CHARACTER
    if (found === previousIndex + 1) score += SCORE_SEQUENTIAL
    else score -= Math.min(found - cursor, 10) * PENALTY_GAP
    if (isWordBoundary(target, found)) score += SCORE_WORD_START

    previousIndex = found
    cursor = found + 1
  }

  // Shorter haystacks are better matches for the same query.
  score -= Math.min(target.length - query.length, 40)

  return { score, indices }
}

export interface FuzzySearchOptions<T> {
  /** Fields to search, most significant first. */
  fields: (item: T) => readonly (string | undefined)[]
  /** Drop results below this score. */
  threshold?: number
  limit?: number
}

export interface FuzzyResult<T> {
  item: T
  score: number
}

/**
 * Filter and rank a list. Ties preserve the input order, so a caller's own
 * ordering (recents first, say) survives an empty query.
 */
export function fuzzySearch<T>(
  items: readonly T[],
  query: string,
  options: FuzzySearchOptions<T>,
): FuzzyResult<T>[] {
  const { fields, threshold = 0, limit } = options
  const trimmed = query.trim()

  if (trimmed === '') {
    const all = items.map((item) => ({ item, score: 0 }))
    return limit === undefined ? all : all.slice(0, limit)
  }

  const results: (FuzzyResult<T> & { order: number })[] = []

  items.forEach((item, order) => {
    let best: number | null = null
    // Later fields are worth progressively less than the primary label.
    fields(item).forEach((value, fieldIndex) => {
      if (value === undefined || value === '') return
      const match = fuzzyMatch(trimmed, value)
      if (match === null) return
      const adjusted = match.score - fieldIndex * 20
      if (best === null || adjusted > best) best = adjusted
    })

    if (best !== null && best >= threshold) results.push({ item, score: best, order })
  })

  results.sort((a, b) => (b.score === a.score ? a.order - b.order : b.score - a.score))

  const ranked = results.map(({ item, score }) => ({ item, score }))
  return limit === undefined ? ranked : ranked.slice(0, limit)
}
